import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Brain,
  CheckCircle2,
  Database,
  FileSpreadsheet,
  GitMerge,
  HeartPulse,
  Lock,
  Search,
  ShieldCheck,
  Sparkles,
  Zap,
  ChevronRight,
  FileText,
  Loader2,
} from 'lucide-react';
import { apiClient } from '@/api/client';

export interface RemedyDetail {
  name: string;
  short_name?: string;
  full_name?: string;
  family: string;
  keynotes: string[];
  modalities: { agg: string; amel: string };
  matchScore: number;
}

const DEFAULT_SAMPLE_REMEDIES: Record<string, RemedyDetail> = {
  Arnica: {
    name: 'Arnica Montana',
    short_name: 'arn.',
    full_name: 'ARNICA MONTANA',
    family: 'Asteraceae (Compositae)',
    keynotes: [
      'Sensation of sore, bruised feeling all over the body.',
      'Patient declares they are "well" despite severe illness.',
      'Bed or mattress feels excessively hard.',
      'Trauma, contusions, injuries, and muscular overexertion.',
    ],
    modalities: {
      agg: 'By touch, movement, physical exertion, cold damp air.',
      amel: 'By absolute rest, lying down with head low.',
    },
    matchScore: 98,
  },
  Aconitum: {
    name: 'Aconitum Napellus',
    short_name: 'acon.',
    full_name: 'ACONITUM NAPELLUS',
    family: 'Ranunculaceae',
    keynotes: [
      'Sudden and violent onset of symptoms after exposure to cold, dry wind.',
      'Intense anxiety, physical restlessness, and fear of death.',
      'Unquenchable thirst for large quantities of cold water.',
      'High fever with hot, dry skin without perspiration.',
    ],
    modalities: {
      agg: 'Around midnight, cold dry wind, lying on affected side.',
      amel: 'In open fresh air, rest, and after sweating.',
    },
    matchScore: 94,
  },
  Belladonna: {
    name: 'Atropa Belladonna',
    short_name: 'bell.',
    full_name: 'ATROPA BELLADONNA',
    family: 'Solanaceae',
    keynotes: [
      'Sudden and intense congestion with redness and burning heat.',
      'Dilated pupils, full bounding pulse, and throbbing carotid arteries.',
      'Extreme hypersensitivity to light, noise, and jar or motion.',
      'Absence of thirst during high inflammatory fever.',
    ],
    modalities: {
      agg: 'In afternoon (3:00 PM), jarring, bright light, touch.',
      amel: 'Resting in a dark room and in an upright position.',
    },
    matchScore: 91,
  },
  Bryonia: {
    name: 'Bryonia Alba',
    short_name: 'bry.',
    full_name: 'BRYONIA ALBA',
    family: 'Cucurbitaceae',
    keynotes: [
      'Extreme aggravation from the slightest movement; relief from firm pressure.',
      'Excessive dryness of all mucous membranes with intense thirst.',
      'Stitching, sharp pains in serous membranes and joints.',
      'Marked irritability and anxiety about business affairs.',
    ],
    modalities: {
      agg: 'By any movement, heat, early morning.',
      amel: 'By absolute rest, pressure on painful side, cold drinks.',
    },
    matchScore: 96,
  },
  'Nux Vomica': {
    name: 'Nux Vomica',
    short_name: 'nux-v.',
    full_name: 'NUX VOMICA',
    family: 'Loganiaceae',
    keynotes: [
      'Nervous system hypersensitivity; irritable, impatient, and ambitious patient.',
      'Ailments from overwork, excessive coffee, alcohol, or medications.',
      'Chilly patient; chills upon the slightest motion or uncovery.',
      'Frequent ineffectual urge to defecate or urinate.',
    ],
    modalities: {
      agg: 'Morning upon waking, after eating, cold air, stimulants.',
      amel: 'Resting in bed, evening quiet, wrapping up head.',
    },
    matchScore: 89,
  },
};

export function LandingPagePro() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('Arnica');
  const [remediesList, setRemediesList] = useState<RemedyDetail[]>(Object.values(DEFAULT_SAMPLE_REMEDIES));
  const [selectedRemedy, setSelectedRemedy] = useState<RemedyDetail>(DEFAULT_SAMPLE_REMEDIES.Arnica);
  const [suggestions, setSuggestions] = useState<RemedyDetail[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch full 650+ remedy catalog from Neo4j on mount
  useEffect(() => {
    let isMounted = true;
    const fetchCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const response = await apiClient.get('/remedies/catalog');
        if (isMounted && response.data?.remedies && Array.isArray(response.data.remedies) && response.data.remedies.length > 0) {
          setRemediesList(response.data.remedies);
          const arnicaInDb = response.data.remedies.find((r: RemedyDetail) =>
            r.name.toLowerCase().includes('arnica') || (r.short_name && r.short_name.toLowerCase().includes('arn'))
          );
          if (arnicaInDb) {
            setSelectedRemedy(arnicaInDb);
          }
        }
      } catch {
        // Local fallback samples remain active
      } finally {
        if (isMounted) {
          setLoadingCatalog(false);
        }
      }
    };

    fetchCatalog();
    return () => {
      isMounted = false;
    };
  }, []);

  // Update autocomplete suggestions when typing
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }

    const query = searchTerm.trim().toLowerCase();
    const filtered = remediesList.filter((r) =>
      r.name.toLowerCase().includes(query) ||
      (r.full_name && r.full_name.toLowerCase().includes(query)) ||
      (r.short_name && r.short_name.toLowerCase().includes(query))
    );

    setSuggestions(filtered.slice(0, 8));
  }, [searchTerm, remediesList]);

  // Click outside listener for autocomplete dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectRemedy = (remedy: RemedyDetail) => {
    setSelectedRemedy(remedy);
    setSearchTerm(remedy.name);
    setIsDropdownOpen(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSelectRemedy(suggestions[0]);
    } else {
      const query = searchTerm.trim().toLowerCase();
      const match = remediesList.find((r) =>
        r.name.toLowerCase().includes(query) ||
        (r.full_name && r.full_name.toLowerCase().includes(query)) ||
        (r.short_name && r.short_name.toLowerCase().includes(query))
      );
      if (match) {
        handleSelectRemedy(match);
      }
    }
  };

  const handleSelectPill = (remedyName: string) => {
    const found = remediesList.find((r) =>
      r.name.toLowerCase().includes(remedyName.toLowerCase()) ||
      (r.full_name && r.full_name.toLowerCase().includes(remedyName.toLowerCase())) ||
      (r.short_name && r.short_name.toLowerCase().includes(remedyName.toLowerCase()))
    ) || DEFAULT_SAMPLE_REMEDIES[remedyName];

    if (found) {
      handleSelectRemedy(found);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-emerald-500 selection:text-white font-sans relative overflow-x-hidden">
      {/* Background Soft Glow Spotlights */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[550px] bg-gradient-to-tr from-emerald-200/30 via-teal-100/40 to-cyan-200/30 blur-[140px] rounded-full pointer-events-none -z-0 animate-pulse-slow" />
      <div className="absolute top-[40%] right-[-10%] w-[600px] h-[600px] bg-emerald-100/40 blur-[160px] rounded-full pointer-events-none -z-0" />
      <div className="absolute top-[75%] left-[-10%] w-[600px] h-[600px] bg-cyan-100/40 blur-[160px] rounded-full pointer-events-none -z-0" />

      {/* 1. FLOATING GLASS NAVBAR */}
      <header className="sticky top-4 z-50 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-panel-light rounded-2xl px-5 py-3 flex items-center justify-between shadow-lg shadow-slate-200/50 border border-slate-200/80">
          <button
            type="button"
            className="flex items-center gap-3 cursor-pointer text-left bg-transparent border-0 p-0"
            onClick={() => navigate('/')}
          >
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/20">
              <Activity className="w-5 h-5 font-bold" />
            </div>
            <div>
              <span className="text-lg font-extrabold tracking-tight text-slate-900">
                SimilDOC <span className="text-emerald-600 font-semibold">PRO</span>
              </span>
              <span className="hidden sm:inline-block ml-2 px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                Neo4j + Gemini 2.5
              </span>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-emerald-600 transition-colors">
              Features
            </a>
            <a href="#demo" className="hover:text-emerald-600 transition-colors">
              Interactive Search
            </a>
            <a href="#workflow" className="hover:text-emerald-600 transition-colors">
              Clinical Workflow
            </a>
            <a href="#security" className="hover:text-emerald-600 transition-colors">
              E2EE Security
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 rounded-xl shadow-md shadow-emerald-600/20 hover:shadow-emerald-600/35 transition-all flex items-center gap-1.5"
            >
              Start consultation <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* 2. HERO SECTION */}
      <section className="relative pt-16 pb-20 md:pt-28 md:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Pulsing AI Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-light border border-emerald-200 text-emerald-800 text-xs sm:text-sm font-medium mb-8 glow-pill-light">
          <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
          <span>AI-Powered Homeopathy Solution for Clinical Precision</span>
          <span className="hidden sm:inline-block bg-emerald-100 px-2 py-0.5 rounded-full text-[11px] font-bold text-emerald-800">
            v2.0
          </span>
        </div>

        {/* High-Impact Typography Headline (A1 Requirement) */}
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1] max-w-5xl mx-auto">
          AI-Powered Homeopathy Solution for{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
            Clinical Precision powered by Knowledge Graphs
          </span>
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-normal">
          Professional tools for homeopathic practice management. Combine deterministic Neo4j graph reasoning, instant symptom matching, and End-to-End Encryption (E2EE).
        </p>

        {/* Call to Action Buttons (A2 Requirement: Start consultation) */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 rounded-2xl shadow-xl shadow-emerald-600/25 hover:shadow-emerald-600/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
          >
            Start consultation <ArrowRight className="w-5 h-5" />
          </button>
          <a
            href="#demo"
            className="w-full sm:w-auto px-8 py-4 text-base font-semibold text-slate-700 glass-card-light hover:bg-white rounded-2xl border border-slate-200/90 hover:border-emerald-500/40 transition-all flex items-center justify-center gap-2"
          >
            <Search className="w-5 h-5 text-emerald-600" /> Check Remedy Database
          </a>
        </div>

        {/* Key trust bullets (A4, A5 Requirement: removed test free, 650+ remedies) */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs sm:text-sm text-slate-500 font-medium">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-teal-600" /> AES-256 Client-Side E2EE
          </div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-cyan-600" /> Database with 650+ Remedies
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Deterministic Neo4j Graph Engine
          </div>
        </div>
      </section>

      {/* 3. INTERACTIVE PRODUCT DEMO SECTION (A3 Requirement: Database connected autocomplete & preview) */}
      <section id="demo" className="py-16 md:py-24 bg-white border-y border-slate-200/80 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Check a homeopathic remedy in our database and learn its main characteristics
            </h2>
            <p className="mt-3 text-slate-600 text-base sm:text-lg">
              Our database contains over 650 homeopathic remedies with modalities, keynotes, and repertory links.
            </p>
          </div>

          {/* Interactive Search Box with Auto-complete Dropdown */}
          <div className="max-w-2xl mx-auto mb-10 relative" ref={dropdownRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative flex items-center">
                <Search className="absolute left-4 w-5 h-5 text-emerald-600" />
                <input
                  type="text"
                  value={searchTerm}
                  onFocus={() => setIsDropdownOpen(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  placeholder="Search among 650+ remedies (e.g. Arnica, Belladonna, Abies, Nux Vomica)..."
                  className="w-full pl-12 pr-28 py-4 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 border border-slate-200 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none text-base transition-all shadow-sm"
                />
                <button
                  type="submit"
                  className="absolute right-2.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl text-sm transition-colors shadow-md shadow-emerald-600/20 flex items-center gap-1"
                >
                  {loadingCatalog && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Search
                </button>
              </div>
            </form>

            {/* Suggestions Dropdown */}
            {isDropdownOpen && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl z-30 max-h-72 overflow-y-auto divide-y divide-slate-100">
                {suggestions.map((remedy) => (
                  <div
                    key={remedy.short_name || remedy.name}
                    onClick={() => handleSelectRemedy(remedy)}
                    className="px-4 py-3 hover:bg-emerald-50/70 cursor-pointer flex items-center justify-between transition-colors text-left"
                  >
                    <div>
                      <span className="font-semibold text-slate-900 text-sm block">
                        {remedy.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {remedy.family} {remedy.short_name ? `• ${remedy.short_name}` : ''}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded-full">
                      Match: {remedy.matchScore}%
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Pills */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">Frequently checked:</span>
              {['Arnica', 'Aconitum', 'Belladonna', 'Bryonia', 'Nux Vomica'].map((pill) => {
                const isActive = selectedRemedy.name.toLowerCase().includes(pill.toLowerCase()) ||
                  (selectedRemedy.short_name && selectedRemedy.short_name.toLowerCase().includes(pill.toLowerCase()));
                return (
                  <button
                    key={pill}
                    type="button"
                    onClick={() => handleSelectPill(pill)}
                    className={`px-3 py-1 rounded-full border transition-all ${
                      isActive
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700 font-semibold shadow-xs'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                  >
                    {pill}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Remedy Preview Card */}
          <div className="max-w-4xl mx-auto glass-panel-light rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl relative overflow-hidden bg-white">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100/50 blur-2xl rounded-full pointer-events-none" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
              <div>
                <span className="text-xs font-semibold text-emerald-700 tracking-wider uppercase">
                  Selected Remedy Profile
                </span>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">
                  {selectedRemedy.name}
                </h3>
                <span className="text-sm text-slate-500">
                  {selectedRemedy.family} {selectedRemedy.short_name ? `(${selectedRemedy.short_name})` : ''}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-3.5 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-sm flex items-center gap-1.5">
                  <Zap className="w-4 h-4 fill-emerald-600 text-emerald-600" /> Match Score: {selectedRemedy.matchScore}%
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/account')}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl shadow-sm transition-all"
                >
                  Use in Consultation
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 pt-6">
              {/* Keynotes */}
              <div>
                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-emerald-600" /> Key Clinical Symptoms
                </h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  {selectedRemedy.keynotes && selectedRemedy.keynotes.length > 0 ? (
                    selectedRemedy.keynotes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-emerald-600 font-bold">•</span>
                        <span>{item}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-slate-400 italic">No specific keynote symptoms listed.</li>
                  )}
                </ul>
              </div>

              {/* Modalities */}
              <div className="space-y-4">
                <div className="bg-red-50/60 rounded-2xl p-4 border border-red-100">
                  <span className="text-xs font-bold text-red-700 uppercase tracking-wider block mb-1">
                    Aggravation Modality
                  </span>
                  <p className="text-sm text-slate-700">
                    {selectedRemedy.modalities?.agg || 'Aggravation parameters defined in Materia Medica.'}
                  </p>
                </div>
                <div className="bg-emerald-50/60 rounded-2xl p-4 border border-emerald-100">
                  <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block mb-1">
                    Amelioration Modality
                  </span>
                  <p className="text-sm text-slate-700">
                    {selectedRemedy.modalities?.amel || 'Amelioration parameters defined in Materia Medica.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. METRIC STRIP (A4 & A6 Requirements: +25,000 Symptoms, 650+ Remedies) */}
      <section className="py-12 border-b border-slate-200/80 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4 rounded-2xl glass-card-light border border-slate-200">
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-600 font-mono">
                +25,000
              </div>
              <div className="mt-1 text-xs sm:text-sm font-medium text-slate-600">
                Indexed Symptoms in Neo4j Graph
              </div>
            </div>
            <div className="p-4 rounded-2xl glass-card-light border border-slate-200">
              <div className="text-3xl sm:text-4xl font-extrabold text-teal-600 font-mono">
                650+
              </div>
              <div className="mt-1 text-xs sm:text-sm font-medium text-slate-600">
                Homeopathic Remedies
              </div>
            </div>
            <div className="p-4 rounded-2xl glass-card-light border border-slate-200">
              <div className="text-3xl sm:text-4xl font-extrabold text-cyan-600 font-mono">
                256-bit
              </div>
              <div className="mt-1 text-xs sm:text-sm font-medium text-slate-600">
                AES-GCM E2EE Data Security
              </div>
            </div>
            <div className="p-4 rounded-2xl glass-card-light border border-slate-200">
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-700 font-mono">
                100%
              </div>
              <div className="mt-1 text-xs sm:text-sm font-medium text-slate-600">
                Deterministic Reasoning
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENTO GRID LAYOUT (A7, A8, A9: SimilDOC PRO & removed Knowledge Books card) */}
      <section id="features" className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-xs font-bold text-emerald-700 tracking-widest uppercase bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            Why SimilDOC PRO?
          </span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mt-4">
            Comprehensive solution for professionals
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            Designed for homeopathic practitioners requiring scientific accuracy, speed, and privacy.
          </p>
        </div>

        {/* Grid Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Neo4j Knowledge Graph */}
          <div className="md:col-span-2 glass-card-light rounded-3xl p-8 border border-slate-200 relative overflow-hidden bg-white group">
            <div className="p-3 w-fit rounded-2xl bg-emerald-50 text-emerald-600 mb-6 border border-emerald-200">
              <GitMerge className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900">Neo4j Knowledge Graph</h3>
            <p className="mt-3 text-slate-600 leading-relaxed text-base">
              Unlike generic generative LLMs that risk clinical hallucinations, our platform relies on a <strong>Neo4j Clinical Knowledge Graph</strong>. Every remedy recommendation is calculated via deterministic relationships between symptoms, modalities, and rubrics.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
              <span className="px-3 py-1 rounded-lg bg-slate-100 border border-slate-200">
                • Zero Hallucinations
              </span>
              <span className="px-3 py-1 rounded-lg bg-slate-100 border border-slate-200">
                • Optimized Cypher Queries
              </span>
              <span className="px-3 py-1 rounded-lg bg-slate-100 border border-slate-200">
                • Full Auditability
              </span>
            </div>
          </div>

          {/* Card 2: Gemini AI Integration */}
          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 relative overflow-hidden bg-white group">
            <div className="p-3 w-fit rounded-2xl bg-teal-50 text-teal-600 mb-6 border border-teal-200">
              <Sparkles className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Gemini 2.5 AI Assistant</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Analyzes free-text patient narratives in everyday language and automatically maps them to standardized repertory terms.
            </p>
          </div>

          {/* Card 3: End-to-End Encryption */}
          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 relative overflow-hidden bg-white group">
            <div className="p-3 w-fit rounded-2xl bg-cyan-50 text-cyan-600 mb-6 border border-cyan-200">
              <Lock className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Data Security (E2EE)</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              AES-GCM 256-bit encryption keys are derived locally in the practitioner's browser. Neither servers nor third parties can read patient records.
            </p>
          </div>

          {/* Card 4: Cross-Search & Remedy Comparison */}
          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 relative overflow-hidden bg-white group">
            <div className="p-3 w-fit rounded-2xl bg-emerald-50 text-emerald-600 mb-6 border border-emerald-200">
              <FileSpreadsheet className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Cross-Search & Comparison</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Advanced repertorization with AND/OR logic. Analyze differences and similarities between candidate remedies in a clear side-by-side table.
            </p>
          </div>

          {/* Card 5: Automatic Summaries & PDF Export */}
          <div className="glass-card-light rounded-3xl p-8 border border-slate-200 relative overflow-hidden bg-white group">
            <div className="p-3 w-fit rounded-2xl bg-cyan-50 text-cyan-600 mb-6 border border-cyan-200">
              <FileText className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Automatic Summaries & PDF Export</h3>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              System creates structured consultation summaries with symptoms and generates professional PDF documents with practice header.
            </p>
          </div>
        </div>
      </section>

      {/* 6. SECURITY & PRIVACY BANNER */}
      <section id="security" className="py-16 bg-slate-100/80 border-y border-slate-200 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-panel-light rounded-3xl p-8 sm:p-12 border border-emerald-200 flex flex-col lg:flex-row items-center justify-between gap-8 shadow-xl relative overflow-hidden bg-white">
            <div className="space-y-4 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
                <ShieldCheck className="w-4 h-4" /> GDPR & HIPAA-Ready Framework
              </div>
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                Encrypted connections and secure patient data storage
              </h2>
              <p className="text-slate-600 text-base leading-relaxed">
                All sensitive clinical records (symptoms, history, and private notes) are encrypted locally using AES-GCM prior to storage. Complete privacy control guaranteed.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 pt-2 text-sm text-slate-600 font-medium">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Local browser master key derivation
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Patient audit consent logging
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 text-center lg:text-right">
              <button
                type="button"
                onClick={() => navigate('/account')}
                className="px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 rounded-2xl shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
              >
                Create Protected Account <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 7. BOTTOM CTA SECTION */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <div className="glass-card-light rounded-3xl p-10 sm:p-16 border border-slate-200 relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-emerald-50/30 shadow-xl">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight max-w-3xl mx-auto">
            Transform your homeopathic practice today
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
            Join medical homeopaths leveraging the power of Knowledge Graphs and AI precision.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="w-full sm:w-auto px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 rounded-2xl shadow-xl shadow-emerald-600/25 hover:shadow-emerald-600/40 transition-all flex items-center justify-center gap-2"
            >
              Start consultation <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* 8. FOOTER (A9 Requirement: SimilDOC PRO) */}
      <footer className="border-t border-slate-200 bg-white py-12 text-slate-500 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-900 font-bold text-lg">
                <HeartPulse className="w-5 h-5 text-emerald-600" /> SimilDOC PRO
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Professional tools for homeopathic practice management powered by Knowledge Graphs and AI.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Product
              </h4>
              <ul className="space-y-2 text-xs">
                <li>
                  <a href="#features" className="hover:text-emerald-600 transition-colors">
                    Neo4j Graph
                  </a>
                </li>
                <li>
                  <a href="#demo" className="hover:text-emerald-600 transition-colors">
                    Remedy Database
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-emerald-600 transition-colors">
                    Data Security E2EE
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Resources
              </h4>
              <ul className="space-y-2 text-xs">
                <li>
                  <button type="button" onClick={() => navigate('/account')} className="hover:text-emerald-600">
                    Patient Profile
                  </button>
                </li>
                <li>
                  <button type="button" onClick={() => navigate('/intake')} className="hover:text-emerald-600">
                    Clinical Intake
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-3">
                Legal & Documents
              </h4>
              <ul className="space-y-2 text-xs">
                <li>
                  <span className="hover:text-slate-700">Terms of Service</span>
                </li>
                <li>
                  <span className="hover:text-slate-700">Privacy Policy</span>
                </li>
                <li>
                  <span className="hover:text-slate-700">E2EE Consent</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Medical Disclaimer Banner */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500 mb-8 leading-relaxed">
            <span className="font-bold text-slate-700">Medical Disclaimer:</span> SimilDOC PRO is a clinical decision support assistant based on homeopathic repertories and AI models. It does not replace professional medical diagnosis or prescribed treatments by a licensed healthcare provider.
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center text-xs text-slate-400 pt-4 border-t border-slate-100">
            <div>© {new Date().getFullYear()} SimilDOC PRO. All rights reserved.</div>
            <div className="mt-2 sm:mt-0 flex gap-4">
              <span>Neo4j Graph Database</span>
              <span>•</span>
              <span>Google Gemini 2.5</span>
              <span>•</span>
              <span>AES-GCM 256</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
